import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import styles from './FilterControls.module.css';

export default function FilterControls({
    timeFrame,
    customDateRange,
    selectedCategories,
    searchQuery,
    categories,
    onTimeFrameChange,
    onDateRangeChange,
    onCategoryChange,
    onSearchChange,
    filteredQRsCount,
    qrsWithScansCount,
    isFetchingNewData = false,
    isProcessingData = false
}) {
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showTimeFrameDropdown, setShowTimeFrameDropdown] = useState(false);
    const [tempSelectedCategories, setTempSelectedCategories] = useState(selectedCategories);
    const dropdownRef = useRef(null);
    const timeFrameDropdownRef = useRef(null);



    // Handle clicking outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
            if (timeFrameDropdownRef.current && !timeFrameDropdownRef.current.contains(event.target)) {
                setShowTimeFrameDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update temp categories when prop changes
    useEffect(() => {
        setTempSelectedCategories(selectedCategories);
    }, [selectedCategories]);

    const handleCategoryToggle = (categoryId) => {
        setTempSelectedCategories(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
    };

    const applyCategoryFilter = () => {
        onCategoryChange(tempSelectedCategories);
        setShowCategoryDropdown(false);
    };

    const clearAllCategories = () => {
        setTempSelectedCategories([]);
    };

    const selectAllCategories = () => {
        if (categories && categories.length > 0) {
            setTempSelectedCategories(categories.map(cat => cat.id));
        }
    };

    const getSelectedCategoriesText = () => {
        if (!categories || categories.length === 0) return 'No Categories Available';
        if (selectedCategories.length === 0) return 'All Categories';
        if (selectedCategories.length === 1) {
            const category = categories.find(cat => cat.id === selectedCategories[0]);
            return category ? category.name : 'Unknown Category';
        }
        return `${selectedCategories.length} Categories Selected`;
    };

    const getSelectedTimeFrameText = () => {
        const option = timeFrameOptions.find(opt => opt.value === timeFrame);
        return option ? option.label : 'Select Time Range';
    };

    const handleTimeFrameSelect = (value) => {
        onTimeFrameChange(value);
        setShowTimeFrameDropdown(false);
    };

    const timeFrameOptions = [
        { value: '1d', label: 'Today' },
        { value: '7d', label: 'Last 7 Days' },
        { value: '30d', label: 'Last 30 Days' },
        { value: '90d', label: 'Last 90 Days' },
        { value: 'all', label: 'All Time' },
        { value: 'custom', label: 'Custom Range' }
    ];

    return (
        <div className={styles.container}>
            {/* Loading Indicator */}
            {(isFetchingNewData || isProcessingData) && (
                <div className={styles.loadingIndicator}>
                    <div className={styles.loadingSpinner}></div>
                    {isFetchingNewData ? 'Fetching data...' : 'Processing...'}
                </div>
            )}
            
            <div className={styles.filtersRow}>
                {/* Time Frame Section */}
                <div className={styles.timeFrameSection}>
                    <h4 className={styles.sectionHeading}>Time Period</h4>
                    <div className={styles.filterGroup} ref={timeFrameDropdownRef}>
                        <label className={styles.label}>Select Time Range</label>
                        <div 
                            className={styles.categorySelector}
                            onClick={() => setShowTimeFrameDropdown(!showTimeFrameDropdown)}
                        >
                            <span className={styles.categoryText}>
                                {getSelectedTimeFrameText()}
                            </span>
                            <div className={`${styles.dropdownArrow} ${showTimeFrameDropdown ? styles.open : ''}`}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <polyline points="6,9 12,15 18,9"></polyline>
                                </svg>
                            </div>
                        </div>

                        {showTimeFrameDropdown && (
                            <div className={styles.categoryDropdown}>
                                <div className={styles.timeFrameList}>
                                    {timeFrameOptions.map(option => (
                                        <div 
                                            key={option.value} 
                                            className={`${styles.timeFrameItem} ${timeFrame === option.value ? styles.selected : ''}`}
                                            onClick={() => handleTimeFrameSelect(option.value)}
                                        >
                                            <span className={styles.timeFrameLabel}>{option.label}</span>
                                            {timeFrame === option.value && (
                                                <div className={styles.checkIcon}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <polyline points="20,6 9,17 4,12"></polyline>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Custom Date Range */}
                    {timeFrame === 'custom' && (
                        <div className={styles.customDateRange}>
                            <div className={styles.filterGroup}>
                                <label className={styles.label}>Date Range</label>
                                <DatePicker
                                    selectsRange
                                    startDate={customDateRange[0]}
                                    endDate={customDateRange[1]}
                                    onChange={onDateRangeChange}
                                    maxDate={new Date()}
                                    dateFormat="MMM dd, yyyy"
                                    placeholderText="Select date range"
                                    className={styles.datePicker}
                                    isClearable
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Divider */}
                <div className={styles.divider}></div>

                {/* QR Filters Section */}
                <div className={styles.qrFiltersSection}>
                    <h4 className={styles.sectionHeading}>Filter Your QRs</h4>
                    <div className={styles.qrFiltersGrid}>
                        {/* Search Input */}
                        <div className={styles.filterGroup}>
                            <label className={styles.label}>Search by Name</label>
                            <div className={styles.searchContainer}>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                    placeholder="Search QR names..."
                                    className={styles.searchInput}
                                />
                                <div className={styles.searchIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <path d="m21 21-4.35-4.35"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Category Filter */}
                        <div className={styles.filterGroup} ref={dropdownRef}>
                            <label className={styles.label}>Filter by Category</label>
                            <div 
                                className={styles.categorySelector}
                                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                            >
                                <span className={styles.categoryText}>
                                    {getSelectedCategoriesText()}
                                </span>
                                <div className={`${styles.dropdownArrow} ${showCategoryDropdown ? styles.open : ''}`}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <polyline points="6,9 12,15 18,9"></polyline>
                                    </svg>
                                </div>
                            </div>

                            {showCategoryDropdown && (
                                <div className={styles.categoryDropdown}>
                                    <div className={styles.dropdownHeader}>
                                        <button 
                                            onClick={selectAllCategories}
                                            className={styles.dropdownAction}
                                            type="button"
                                        >
                                            Select All
                                        </button>
                                        <button 
                                            onClick={clearAllCategories}
                                            className={styles.dropdownAction}
                                            type="button"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    
                                    <div className={styles.categoryList}>
                                        {!categories || categories.length === 0 ? (
                                            <div style={{ padding: '1rem', color: '#6b7280', textAlign: 'center' }}>
                                                No categories available
                                            </div>
                                        ) : (
                                            categories.map(category => (
                                                <label key={category.id} className={styles.categoryItem}>
                                                    <input
                                                        type="checkbox"
                                                        checked={tempSelectedCategories.includes(category.id)}
                                                        onChange={() => handleCategoryToggle(category.id)}
                                                        className={styles.checkbox}
                                                    />
                                                    <span className={styles.categoryName}>{category.name}</span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                    
                                    <div className={styles.dropdownFooter}>
                                        <button 
                                            onClick={applyCategoryFilter}
                                            className={styles.applyButton}
                                            type="button"
                                        >
                                            Apply Filter
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(selectedCategories.length > 0 || searchQuery.trim()) && (
                        <div className={styles.activeFilters}>
                            <span className={styles.activeFiltersLabel}>Active Filters:</span>
                            
                            {searchQuery.trim() && (
                                <div className={styles.filterTag}>
                                    Search: "{searchQuery}"
                                    <button 
                                        onClick={() => onSearchChange('')}
                                        className={styles.removeFilter}
                                        type="button"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                            
                            {selectedCategories.map(categoryId => {
                                const category = categories.find(cat => cat.id === categoryId);
                                return category ? (
                                    <div key={categoryId} className={styles.filterTag}>
                                        {category.name}
                                        <button 
                                            onClick={() => onCategoryChange(selectedCategories.filter(id => id !== categoryId))}
                                            className={styles.removeFilter}
                                            type="button"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* QR Count Display */}
            {filteredQRsCount !== undefined && (
                <div className={styles.qrCount}>
                    <div className={styles.countBadge}>
                        <div className={styles.countStats}>
                            <div className={styles.mainCount}>
                                <strong>{filteredQRsCount}</strong> QR{filteredQRsCount !== 1 ? ' codes' : ' code'} selected
                            </div>
                            {qrsWithScansCount !== undefined && filteredQRsCount > 0 && (
                                <div className={styles.scansInfo}>
                                    <strong>{qrsWithScansCount}</strong> of these have scan data in the selected time period
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
} 